/**
 * Overview-page hooks (PR 2 of the dashboard standards).
 *
 * The previous monolithic `useOverviewSummary` is gone. The redesigned
 * page is backed by four period-aware hooks that share a common cache
 * key shape (`['overview', <slice>, period, …]`) so a period change
 * invalidates every chart in lock-step.
 *
 * Sequencing: `useOverviewKpis` is the anchor query — it returns the
 * warehouse `data_anchor` (max(transaction_time)) along with the KPI
 * payload. The other three hooks accept the period directly and can
 * fire in parallel; they don't *need* the anchor because the backend
 * resolves it independently on each request. The anchor is surfaced for
 * the "Data as of …" header label only.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Period } from '@/lib/timePeriod';
import type { VehicleDim } from '@/components/shared/VehicleDimensionSelect';
import type {
  EfficiencyTrendResponse,
  OverviewKpisResponse,
  SpendTrendResponse,
  TopAssetsCategory,
  TopAssetsResponse,
  TopAssetsSortBy,
} from '@/types/overview';

const STALE_MS = 60_000;

// ---------------------------------------------------------------------------
// /overview/kpis
// ---------------------------------------------------------------------------

export function useOverviewKpis(
  period: Period,
  weighted = true,
): UseQueryResult<OverviewKpisResponse, Error> {
  return useQuery<OverviewKpisResponse, Error>({
    queryKey: ['overview', 'kpis', period, weighted],
    queryFn: async () => {
      const { data } = await api.get<OverviewKpisResponse>('/overview/kpis', {
        params: { period, weighted },
      });
      return data;
    },
    staleTime: STALE_MS,
  });
}

// ---------------------------------------------------------------------------
// /overview/spend-trend
// ---------------------------------------------------------------------------

export function useOverviewSpendTrend(
  period: Period,
  dim: VehicleDim,
): UseQueryResult<SpendTrendResponse, Error> {
  return useQuery<SpendTrendResponse, Error>({
    queryKey: ['overview', 'spend-trend', period, dim],
    queryFn: async () => {
      const { data } = await api.get<SpendTrendResponse>(
        '/overview/spend-trend',
        { params: { period, dim } },
      );
      return data;
    },
    staleTime: STALE_MS,
  });
}

// ---------------------------------------------------------------------------
// /overview/efficiency-trend
// ---------------------------------------------------------------------------

export function useOverviewEfficiencyTrend(
  period: Period,
): UseQueryResult<EfficiencyTrendResponse, Error> {
  return useQuery<EfficiencyTrendResponse, Error>({
    queryKey: ['overview', 'efficiency-trend', period],
    queryFn: async () => {
      const { data } = await api.get<EfficiencyTrendResponse>(
        '/overview/efficiency-trend',
        { params: { period } },
      );
      return data;
    },
    staleTime: STALE_MS,
  });
}

// ---------------------------------------------------------------------------
// /overview/top-assets
// ---------------------------------------------------------------------------

export function useOverviewTopAssets(
  period: Period,
  sortBy: TopAssetsSortBy,
  category: TopAssetsCategory,
  limit = 10,
): UseQueryResult<TopAssetsResponse, Error> {
  return useQuery<TopAssetsResponse, Error>({
    queryKey: ['overview', 'top-assets', period, sortBy, category, limit],
    queryFn: async () => {
      const { data } = await api.get<TopAssetsResponse>(
        '/overview/top-assets',
        { params: { period, sort_by: sortBy, category, limit } },
      );
      return data;
    },
    staleTime: STALE_MS,
  });
}
